(() => {
  'use strict';
  const source = document.getElementById('OhUpcartDesign');
  if (!source || window.ohUpcartDesignLoaded) return;
  window.ohUpcartDesignLoaded = true;
  const copy = JSON.parse(source.textContent);
  const settings = window.upcartSettings;
  if (settings?.upcartSettings && settings?.upcartEditorSettings) {
    const general = settings.upcartSettings;
    const editor = settings.upcartEditorSettings;
    Object.assign(general.translations, { cartTitle: copy.title, checkout: copy.checkout, totalSavings: copy.savings, addText: copy.add });
    general.savingsRowBehaviour = 'show_discount_and_compare_at_savings';
    editor.settingsModule.fields.enableSubtotalLine = true;
    editor.upsellsModulePosition = 'bottom';
    editor.upsellsDirection = 'horizontal';
    editor.showUpsellItemsAlreadyInCart = false;
    editor.upsellsTitle = copy.recommendations;
    const tiers = editor.rewardsTiers?.tiers;
    if (tiers?.length === 1 && tiers[0].targetType === 'shipping' && editor.rewardsBasis === 'cartTotal') {
      tiers[0].textBeforeReachingForCartTotal = copy.thresholdRemaining;
      editor.rewardsEditorAfterText = copy.thresholdReached;
    }
  }

  const subscriptions = new Set();
  let root;
  let observer;
  let frame;
  let opened = false;
  let returnFocus;
  let savedScroll = null;
  let scrollExpiry = 0;
  const focusable = 'a[href], button, input, select, textarea, [tabindex="0"]';
  const tokens = ['--font-family', '--body-font-weight', '--heading-font-family-2', '--colorTextBody', '--colorBtnPrimary', '--colorBtnPrimaryText', '--buttonRadius', '--buttonFontSize', '--buttonFontWeight', '--buttonLetterSpacing', '--colorXXlightTextBody', '--secondaryColorBody', '--progressBar', '--progressTint', '--progressInk'];

  function element(tag, className, text) {
    const node = document.createElement(tag);
    node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(() => { frame = null; enhance(); });
  }

  function installStyles() {
    const sourceLink = document.querySelector('link[data-oh-upcart-styles]');
    if (!root?.host || !sourceLink) return;
    let link = root.querySelector('link[data-oh-upcart-styles]');
    if (!link) {
      link = sourceLink.cloneNode();
      root.append(link);
    }
    if (link.href !== sourceLink.href) link.href = sourceLink.href;
    const styles = getComputedStyle(document.documentElement);
    for (const token of tokens) {
      const value = styles.getPropertyValue(token).trim();
      if (value && root.host.style.getPropertyValue(token) !== value) root.host.style.setProperty(token, value);
    }
  }

  function visibleControls(cart) {
    return [...cart.querySelectorAll(focusable)].filter(node => !node.disabled && !node.closest('[inert]') && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden' && node.tabIndex >= 0);
  }

  function keydown(event) {
    if (!opened) return;
    savedScroll = null;
    const cart = root?.querySelector('.upcart-cart');
    if (!cart) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      window.upcartCloseCart?.();
    }
    if (event.key !== 'Tab') return;
    const controls = visibleControls(cart);
    const active = root.activeElement || document.activeElement;
    const index = controls.indexOf(active);
    if (!controls.length) { event.preventDefault(); cart.focus(); return; }
    if (event.shiftKey && index <= 0) { event.preventDefault(); controls.at(-1).focus(); }
    else if (!event.shiftKey && (index === controls.length - 1 || index < 0)) { event.preventDefault(); controls[0].focus(); }
  }

  function open() {
    if (!opened) {
      returnFocus = document.activeElement;
      opened = true;
      enhance();
      requestAnimationFrame(() => root?.querySelector('.upcart-public-header__close-button')?.focus({ preventScroll: true }));
    }
    schedule();
  }

  function close() {
    opened = false;
    savedScroll = null;
    schedule();
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
  }

  function captureScroll(event) {
    if (!event.target.closest?.('.upcart-public-upsell .upcart-public-component__button')) return;
    savedScroll = root.querySelector('.upcart-cart-body')?.scrollTop ?? null;
    scrollExpiry = Date.now() + 10000;
  }

  function enhance() {
    if (!root) return;
    observer?.disconnect();
    try {
      installStyles();
      const cart = root.querySelector('.upcart-cart');
      if (!cart || !cart.querySelector('.upcart-public-header')) return;
      cart.classList.add('oh-upcart');
      cart.setAttribute('role', 'dialog');
      cart.setAttribute('aria-modal', 'true');
      cart.setAttribute('aria-label', copy.title);
      cart.tabIndex = -1;
      cart.inert = !opened;
      const data = window.upcartGetCart?.();
      const count = data?.items?.reduce((sum, item) => sum + item.quantity, 0);
      const header = cart.querySelector('.upcart-public-header');
      if (Number.isFinite(count)) {
        let counter = header.querySelector('.oh-upcart-count');
        if (!counter) {
          counter = element('span', 'oh-upcart-count');
          counter.setAttribute('aria-live', 'polite');
          header.insertBefore(counter, header.querySelector('.upcart-public-header__close-button'));
        }
        setText(counter, count === 1 ? copy.oneItem : copy.manyItems.replace('[count]', count));
      }
      const checkout = cart.querySelector('.upcart-checkout-button-container');
      const checkoutButton = checkout?.querySelector('.upcart-checkout-button');
      if (checkoutButton && !checkoutButton.querySelector('.oh-upcart-checkout-icon')) {
        const icon = document.getElementById('OhUpcartCheckoutIcon');
        if (icon) checkoutButton.prepend(icon.content.cloneNode(true));
      }
      const actionRow = checkout?.parentElement;
      if (actionRow) {
        actionRow.classList.add('oh-upcart-actions');
        if (!actionRow.querySelector('.oh-upcart-view-cart')) {
          const link = element('a', 'oh-upcart-view-cart', copy.viewCart);
          link.href = copy.cartUrl;
          actionRow.prepend(link);
        }
      }
      const footer = cart.querySelector('.upcart-footer');
      if (footer) {
        const summary = [...footer.children].find(node => node.querySelector('.UpcartDesignSettings__subtotalTextColor'));
        summary?.classList.add('oh-upcart-subtotal');
        if (!footer.querySelector('.oh-upcart-delivery')) {
          const delivery = element('a', 'oh-upcart-delivery', copy.deliveryPending);
          delivery.href = copy.deliveryUrl;
          footer.append(delivery);
        }
        if (!footer.querySelector('.oh-upcart-payments')) {
          const template = document.getElementById('OhUpcartPayments');
          if (template?.content.children.length) footer.append(template.content.cloneNode(true));
        }
      }
      const rewards = cart.querySelector('.upcart-public-rewards__message');
      const fills = cart.querySelectorAll('.upcart-public-component-segmented-progress-bar__segment-fill');
      const progress = cart.querySelector('.upcart-public-rewards__bar');
      if (progress && fills.length === 1) {
        progress.classList.add('oh-upcart-shipping-progress');
        if (!progress.querySelector('.oh-upcart-delivery-endpoint')) {
          const icon = document.getElementById('OhUpcartDeliveryIcon');
          if (icon) {
            const endpoint = element('span', 'oh-upcart-delivery-endpoint');
            endpoint.setAttribute('aria-hidden', 'true');
            endpoint.append(icon.content.cloneNode(true));
            progress.append(endpoint);
          }
        }
        const percentage = Number.parseFloat(fills[0].style.width);
        if (Number.isFinite(percentage)) {
          progress.setAttribute('role', 'progressbar');
          progress.setAttribute('aria-label', rewards?.textContent.trim() || copy.deliveryPending);
          progress.setAttribute('aria-valuemin', '0');
          progress.setAttribute('aria-valuemax', '100');
          progress.setAttribute('aria-valuenow', String(Math.min(100, Math.max(0, percentage))));
        }
      }
      for (const button of cart.querySelectorAll('.upcart-public-component-product-tile__remove-button')) {
        if (!button.querySelector('.oh-upcart-remove-text')) button.append(element('span', 'oh-upcart-remove-text', copy.remove));
      }
      for (const property of cart.querySelectorAll('.upcart-public-cart-items__key-value-pair, .upcart-public-product-properties__item')) {
        property.classList.toggle('oh-upcart-estimated-delivery', /^Estimated delivery\b/i.test(property.textContent.trim()));
      }
      for (const track of cart.querySelectorAll('.upcart-public-component-carousel__track')) {
        const slides = [...track.querySelectorAll('.upcart-public-component-carousel__slide')];
        const target = track.style.transform.match(/translateX\((-?[\d.]+)%\)/);
        const viewport = track.closest('.upcart-public-component-carousel__window');
        if (!viewport || !slides.length) continue;
        const bounds = viewport.getBoundingClientRect();
        const overlap = slides.map(slide => {
          const rect = slide.getBoundingClientRect();
          return Math.max(0, Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left));
        });
        const active = target ? Math.max(0, Math.min(slides.length - 1, Math.round(-Number(target[1]) / 100))) : overlap.indexOf(Math.max(...overlap));
        slides.forEach((slide, index) => { slide.inert = index !== active; });
      }
      const body = cart.querySelector('.upcart-cart-body');
      if (body && savedScroll !== null) {
        if (Date.now() < scrollExpiry) body.scrollTop = savedScroll;
        else savedScroll = null;
      }
    } finally {
      observer?.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'disabled'] });
    }
  }

  function bind() {
    const currentRoot = window.upcartDocumentOrShadowRoot;
    if (currentRoot && currentRoot !== root) {
      observer?.disconnect();
      root?.removeEventListener('click', captureScroll, true);
      root = currentRoot;
      observer = new MutationObserver(schedule);
      root.addEventListener('click', captureScroll, true);
      root.addEventListener('pointerdown', () => { savedScroll = null; }, { passive: true });
      root.addEventListener('transitionend', schedule);
      root.addEventListener('wheel', () => { savedScroll = null; }, { passive: true });
      root.addEventListener('touchstart', () => { savedScroll = null; }, { passive: true });
      enhance();
    }
    const handlers = {
      upcartSubscribeCartLoaded: schedule,
      upcartSubscribeCartUpdated: schedule,
      upcartSubscribeCartOpened: open,
      upcartSubscribeCartClosed: close,
      upcartSubscribeUpsellsRendered: schedule,
      upcartSubscribeUpsellsAddedToCart: schedule
    };
    for (const [name, handler] of Object.entries(handlers)) {
      if (!subscriptions.has(name) && typeof window[name] === 'function') {
        window[name](handler);
        subscriptions.add(name);
      }
    }
  }
  document.addEventListener('keydown', keydown, true);
  window.addEventListener('resize', schedule);
  window.addEventListener('pageshow', bind);
  new MutationObserver(() => { bind(); schedule(); }).observe(document.head, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
  bind();
  const timer = setInterval(() => { bind(); if (root && subscriptions.size === 6) clearInterval(timer); }, 250);
  setTimeout(() => clearInterval(timer), 30000);
})();
